import { useCallback, useEffect, useState } from "react";
import { Minus, Plus, Pencil, Trash2, Ticket, Loader2, Zap, ListPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, ConfirmDialog, Field, LockedTag, Money } from "../components/common";
import { AnakCombobox, PerusahaanSelect, PeriodPicker } from "../components/inputs";
import { api, errMsg } from "../lib/api";
import { currentPeriod, fmtDate, num, rp, todayISO } from "../lib/format";

const VoucherForm = ({ anak, perusahaan, tarif, initial, onSubmit, quick, prefix }) => {
  const [anakId, setAnakId] = useState(initial?.anak_id || "");
  const [perId, setPerId] = useState(initial?.perusahaan_id || "");
  const [tanggal, setTanggal] = useState(initial?.tanggal || todayISO());
  const [tamu, setTamu] = useState(String(initial?.tamu || 1));
  const [saving, setSaving] = useState(false);
  const n = parseInt(tamu, 10) || 0;

  const pickAnak = (id) => {
    setAnakId(id);
    const a = anak.find((x) => x.id === id);
    setPerId(a?.perusahaan_id || "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!anakId) return toast.error("Pilih anak terlebih dahulu");
    if (n < 1) return toast.error("Jumlah tamu minimal 1");
    if (!tanggal) return toast.error("Tanggal wajib diisi");
    setSaving(true);
    const ok = await onSubmit({ anak_id: anakId, perusahaan_id: perId || null, tanggal, tamu: n });
    setSaving(false);
    if (ok && quick) { setAnakId(""); setPerId(""); setTamu("1"); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-[1.4fr_1.2fr_1fr_1fr]" data-testid={`${prefix}-form`}>
      <div className="col-span-2 lg:col-span-1"><Field label="Anak"><AnakCombobox anak={anak.filter((a) => a.aktif || a.id === anakId)} value={anakId} onChange={pickAnak} testid={`${prefix}-anak`} /></Field></div>
      <div className="col-span-2 lg:col-span-1"><Field label="Perusahaan"><PerusahaanSelect perusahaan={perusahaan} value={perId} onChange={setPerId} testid={`${prefix}-perusahaan`} /></Field></div>
      <Field label="Tanggal"><Input type="date" data-testid={`${prefix}-tanggal`} className="h-10 bg-white" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></Field>
      <Field label="Jumlah tamu">
        <div className="flex h-10 items-stretch overflow-hidden rounded-md border border-input bg-white">
          <button type="button" data-testid={`${prefix}-tamu-minus`} onClick={() => setTamu(String(Math.max(1, n - 1)))} className="w-12 text-gray-600 hover:bg-gray-100 active:scale-95" aria-label="Kurangi"><Minus className="mx-auto h-4 w-4" /></button>
          <input data-testid={`${prefix}-tamu`} inputMode="numeric" value={tamu} onChange={(e) => setTamu(e.target.value.replace(/\D/g, "").slice(0, 4))} className="w-full min-w-0 border-x border-input text-center font-mono text-lg font-semibold outline-none" />
          <button type="button" data-testid={`${prefix}-tamu-plus`} onClick={() => setTamu(String(n + 1))} className="w-12 text-gray-600 hover:bg-gray-100 active:scale-95" aria-label="Tambah"><Plus className="mx-auto h-4 w-4" /></button>
        </div>
      </Field>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between col-span-2 lg:col-span-4">
        <p data-testid={`${prefix}-preview`} className="font-mono text-sm text-gray-600">
          {num(n)} voucher × {rp(tarif)} = <span className="font-semibold text-primary">{rp(n * tarif)}</span>
        </p>
        <Button data-testid={`${prefix}-submit`} type="submit" disabled={saving} className="h-10 rounded-full px-5 text-base active:scale-[0.98]">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial ? "Simpan perubahan" : "Simpan voucher"}
        </Button>
      </div>
    </form>
  );
};

export default function VoucherPage() {
  const [anak, setAnak] = useState([]);
  const [perusahaan, setPerusahaan] = useState([]);
  const [tarif, setTarif] = useState(0);
  const [period, setPeriod] = useState(currentPeriod());
  const [fPer, setFPer] = useState("");
  const [fAnak, setFAnak] = useState("");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);


  useEffect(() => {
    api.get("/anak").then((r) => setAnak(r.data));
    api.get("/perusahaan").then((r) => setPerusahaan(r.data));
    api.get("/settings").then((r) => setTarif(r.data.tarif_per_voucher));
  }, []);

  const load = useCallback(() => {
    api.get("/voucher", { params: { periode: period, perusahaan_id: fPer || undefined, anak_id: fAnak || undefined } })
      .then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, fPer, fAnak]);
  useEffect(() => { load(); }, [load]);

  const create = async (body) => {
    try { await api.post("/voucher", body); toast.success("Voucher berhasil ditambahkan"); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const update = async (body) => {
    try { await api.put(`/voucher/${editing.id}`, body); toast.success("Voucher diperbarui"); setEditing(null); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const doDelete = async () => {
    try { await api.delete(`/voucher/${del.id}`); toast.success("Voucher dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  const totalTamu = (items || []).reduce((s, v) => s + v.tamu, 0);
  return (
    <div>
      <PageHeader title="Voucher" testid="voucher-title" subtitle={`1 tamu = 1 voucher = ${rp(tarif)}`}
        actions={<Button asChild variant="outline" className="h-10 rounded-full"><Link to="/voucher/massal" data-testid="voucher-open-massal"><ListPlus className="mr-1.5 h-4 w-4" />Input Massal</Link></Button>} />

      <section className="fade-up relative mb-5 overflow-hidden rounded-2xl border border-primary/15 bg-white p-4 sm:p-4">
        
        <div className="relative">
          <p className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-gray-900"><Zap className="h-4 w-4 text-primary" />Input cepat</p>
          {anak.length === 0 ? <p className="text-sm text-gray-500">Tambahkan anak terlebih dahulu di menu Anak.</p> :
            <VoucherForm anak={anak} perusahaan={perusahaan} tarif={tarif} onSubmit={create} quick prefix="quick-voucher" />}
        </div>
      </section>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <PeriodPicker value={period} onChange={setPeriod} testid="voucher-period" />
        <PerusahaanSelect perusahaan={perusahaan} value={fPer} onChange={setFPer} allowAll testid="voucher-filter-perusahaan" />
        <AnakCombobox anak={anak} value={fAnak} onChange={setFAnak} allowAll testid="voucher-filter-anak" />
      </div>
      <p data-testid="voucher-summary" className="mb-3 text-sm text-gray-500">
        {items ? <>{items.length} entri · <span className="font-mono font-semibold text-gray-900">{num(totalTamu)}</span> voucher · <span className="font-mono font-semibold text-primary">{rp(totalTamu * tarif)}</span></> : "Memuat…"}
      </p>

      {items && items.length === 0 ? <EmptyState icon={Ticket} title="Belum ada voucher" desc="Tidak ada voucher pada filter ini." /> : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {(items || []).map((v, i) => (
            <div key={v.id} data-testid={`voucher-row-${v.id}`} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }} className="fade-up flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
              <div className="flex h-10 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-secondary">
                <span className="font-mono text-lg font-bold leading-none text-primary">{v.tamu}</span>
                <span className="text-[9px] font-semibold uppercase text-primary/70">voucher</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-gray-900">{v.anak_nama || "-"}</p>
                <p className="break-words text-[13px] text-gray-500">{fmtDate(v.tanggal)} · {v.perusahaan_nama || "Tanpa perusahaan"}</p>
              </div>
              <Money value={v.tamu * tarif} className="hidden text-sm text-gray-900 sm:block" />
              {v.locked ? <LockedTag testid={`voucher-locked-${v.id}`} /> : (
                <div className="flex shrink-0">
                  <Button data-testid={`voucher-edit-${v.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => setEditing(v)} aria-label="Ubah"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`voucher-delete-${v.id}`} variant="ghost" size="icon" className="h-9 w-9 text-red-600 hover:bg-red-50" onClick={() => setDel(v)} aria-label="Hapus"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="voucher-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ubah voucher</DialogTitle></DialogHeader>
          {editing && <VoucherForm anak={anak} perusahaan={perusahaan} tarif={tarif} initial={editing} onSubmit={update} prefix="edit-voucher" />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title="Hapus voucher?"
        description={del ? `${del.tamu} voucher milik ${del.anak_nama} tanggal ${fmtDate(del.tanggal)} akan dihapus.` : ""} confirmLabel="Hapus" onConfirm={doDelete} testid="voucher-delete-confirm" />
    </div>
  );
}
